// src/pages/PrivacyPolicy.jsx
import contactBg from '../assets/images/contact-bg.png';
import { Container, Row } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api from '../services/api';

function usePrivacyPolicy() {
  return useQuery({
    queryKey: ['privacyPolicy'],
    queryFn: async () => {
      const { data } = await api.get('/profile-settings/privacy-policy');
      // Expecting: { dataset: { data: { title, short_description, description } } }
      return data?.dataset?.data ?? {};
    },
    staleTime: 5 * 60 * 1000,
    cacheTime: 5 * 60 * 1000,
    retry: 2,
  });
}

const PrivacyPolicy = () => {
  const { data, isLoading, isError, error } = usePrivacyPolicy();

  // Full-screen loading overlay
  if (isLoading) {
    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: '#fff',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999,
        }}
      >
        <img
          src="https://stg-admin.gkcloud.in/storage/settings/backend_favicon_1739509041.png"
          alt="Loading..."
          style={{ width: '100px', height: 'auto', animation: 'pulse 1.5s infinite' }}
        />
        <style>
          {`
            @keyframes pulse {
              0% { transform: scale(1); }
              50% { transform: scale(1.1); }
              100% { transform: scale(1); }
            }
          `}
        </style>
      </div>
    );
  }

  // Full-screen error overlay
  if (isError) {
    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: '#fff',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999,
        }}
      >
        <p>Error loading content: {error?.message || 'Unknown error'}</p>
      </div>
    );
  }

  const title = data?.title || 'Privacy Policy';

  // Prefer short_description (can be HTML). Fallback: trimmed plain-text excerpt of description.
  const shortHTML =
    (data?.short_description && String(data.short_description).trim()) || '';
  const shortFallback =
    !shortHTML && data?.description
      ? String(data.description)
          .replace(/<[^>]+>/g, ' ') // strip HTML
          .replace(/\s+/g, ' ')
          .trim()
          .slice(0, 220) + '…'
      : '';

  return (
    <>
      <section
        className="contact-banner"
        style={{
          backgroundImage: `url(${contactBg})`,
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          marginBottom: '50px',
          position: 'relative',
          overflow: 'hidden',
          paddingTop: '180px',
          minHeight: '450px',
        }}
      >
        <div className="floating-dots">
          {[...Array(10)].map((_, i) => (
            <div className="dot" key={i}></div>
          ))}
        </div>

        <Container>
          <Row className="align-items-center">
            <div className="col-lg-12">
              <div className="section_title top_content text-center ">
                <span className="title_badge mb-1" style={{ background: '#674EDD' }}>
                  {title}
                </span>
                <h2 className="freeLive-Test">{title}</h2>

                {/* short description block (same UI, dynamic) */}
                {shortHTML ? (
                  <p className="mb-0" dangerouslySetInnerHTML={{ __html: shortHTML }} />
                ) : (
                  <p className="mb-0">{shortFallback || '—'}</p>
                )}

                <ul className="banner-nav">
                  <li><Link to="/">Home</Link></li>
                  <li><span>»</span></li>
                  <li><Link to="/privacy-policy">{title}</Link></li>
                </ul>
              </div>
            </div>
          </Row>
        </Container>
      </section>

      {/* Content section (dynamic HTML) */}
      <section className="sourceInformation my-4">
        <Container>
          <Row>
            <div className="col-lg-12 text-center">
              <h3 className="mb-4">{title}</h3>
            </div>

            <div className="col-12">
              <div
                dangerouslySetInnerHTML={{
                  __html: data?.description || '<p>No content available.</p>',
                }}
              />
            </div>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default PrivacyPolicy;